export interface PreIpoApiResponse {
  data: {
    id: number;
    documentId: string;
    title: string;
    isEnabled: boolean;
    isOnlinePaymentEnabled: boolean;
    disclaimer: ParagraphBlock[];
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    deals: PreIpoDeal[];
    mainFaqSection: PreIpoFaq[];
  };
  meta: Record<string, any>;
}

export interface ParagraphBlock {
  type: string;
  children: { text: string; type: string }[];
}

export interface PreIpoDeal {
  id: number;
  brandName: string;
  companyName: string;
  isin: string;
  colors: string[];
  sector: string;
  partnerPricePerShare: string;
  lotSize: string;
  faceValue: string;
  DRHPStatus: string;
  priceTrendBreakup: any[];
  about: ParagraphBlock[];
  priceTrend: any;
  tags: { type: string; label: string }[];
  deal_id: string;
  isLive: boolean;
  isSoldOut: boolean;
  slashedPricePerShare: string;
  brandColor: string;
  kickstarterLotSize: string | null;
  primaryLogo: PreIpoImage;
  secondaryLogo: PreIpoImage;
  cardImage: PreIpoImage;
  financialMetrics: PreIpoFinancialMetric[];
  shareholdingPattern: any[];
  documents: PreIpoDocument[];
  newsSection: PreIpoNews[];
  faqSection: any[];
  shareholdingPatternImage: PreIpoImage;
  SWOTAnalysis: PreIpoSWOT;
}

export interface PreIpoImage {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number | null;
  height: number | null;
  formats: {
    thumbnail?: {
      ext: string;
      url: string;
      hash: string;
      mime: string;
      name: string;
      path: string | null;
      size: number;
      width: number;
      height: number;
      sizeInBytes: number;
    };
  } | null;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

export interface PreIpoLotSizeTrend {
  id: number;
  price: string;
  minimumLotSizeInNumber: string;
  maximumLotSizeInNumber: string;
  trendInStringFormat: string;
}

export interface PreIpoFinancialMetric {
  id: number;
  title: string;
  value: string;
  tooltip: string | null;
  icon: PreIpoImage[];
}

export interface PreIpoDocument {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number | null;
  height: number | null;
  formats: any;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

export interface PreIpoNews {
  id: number;
  title: string;
  subtitle: string;
  link: string;
  image: PreIpoImage;
}

export interface PreIpoSWOT {
  id: number;
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
}

export interface PreIpoFaq {
  id: number;
  question: string;
  answer: string;
}

export async function getPreIpoDetails({ pLevel = 4 } = {}): Promise<PreIpoApiResponse> {
  try {
    const url = `${process.env.REACT_APP_STRAPI_API_ENDPOINT}/app-pre-ipo-page?populate=*&pLevel=${pLevel}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_STRAPI_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching Pre-IPO details:', error);
    throw error;
  }
}

export interface PreIpoInterestPayload {
  partnerName: string;
  dealId: string;
  dealBrandName: string;
  dealCompanyName: string;
  dealIsin: string;
  partnerPrice: number;
  clientPrice: number;
  investmentAmount: number;
  lotSize: number;
  numberOfLots: number;
  totalInvestment: number;
  commission: number;
  remainingAmount: number;
  partnerEmail: string;
  partnerPhone: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  isAgreed: boolean;
  timestamp: string;
}

export async function submitPreIpoInterest(payload: PreIpoInterestPayload): Promise<any> {
  try {
    const url = `${process.env.REACT_APP_STRAPI_API_ENDPOINT}/partner-pre-ipo-interest-captures`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_STRAPI_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: payload }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error submitting Pre-IPO interest:', error);
    throw error;
  }
}
